export class ChatView {
	constructor(state, options = {}) {
		this.state = state;
		this.options = options;
		this.element = null;
		this._unsubscribe = null;
		this._typingTimeout = null;
		this._isTyping = false;
		this._typingIndicator = null;
		this._isConversationClosed = false;
		this._pendingAttachments = [];
	}

	render() {
		this.element = document.createElement('div');
		this.element.className = 'messenger-view messenger-chat-view';

		this._updateContent();

		this._unsubscribe = this.state.subscribe((type, data) => {
			if (
				type === 'messageAdded' &&
				data.conversationId === this.state.activeConversationId
			) {
				this._hideTypingIndicator();
				this._appendMessage(data.message);
				this._scrollToBottom();
			} else if (
				type === 'typingStarted' &&
				data.conversationId === this.state.activeConversationId
			) {
				this._showTypingIndicator(data.userName);
			} else if (
				type === 'typingStopped' &&
				data.conversationId === this.state.activeConversationId
			) {
				this._hideTypingIndicator();
			} else if (
				type === 'conversationUpdated' &&
				data.conversationId === this.state.activeConversationId
			) {
				this._updateContent();
			} else if (
				type === 'messagesUpdate' &&
				data.conversationId === this.state.activeConversationId
			) {
				this._updateContent();
			}
		});

		return this.element;
	}

	_updateContent() {
		const conversation = this.state.getActiveConversation();
		const messages = this.state.getActiveMessages();
		const isNewConversation = !this.state.activeConversationId;
		const isClosed = !isNewConversation && conversation?.status === 'closed';
		this._isConversationClosed = isClosed;

		const messagesHtml =
			messages.length === 0
				? this._renderEmptyState(isNewConversation)
				: messages.map((msg) => this._renderMessage(msg)).join('');

		const title = isNewConversation
			? 'New conversation'
			: conversation?.title || 'Chat with team';
		const placeholder = isNewConversation
			? 'Start typing your message...'
			: isClosed
				? 'Conversation closed'
				: 'Write a message...';

		this.element.innerHTML = `
			<div class="messenger-chat-header">
				<button class="sdk-btn-icon messenger-back-btn" aria-label="Back">
					<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
						<path d="M165.66,202.34a8,8,0,0,1-11.32,11.32l-80-80a8,8,0,0,1,0-11.32l80-80a8,8,0,0,1,11.32,11.32L91.31,128Z"></path>
					</svg>
				</button>
				<div class="messenger-chat-header-info">
					<span class="messenger-chat-title">${title}</span>
				</div>
				<button class="sdk-close-btn" aria-label="Close">
					<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
						<path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path>
					</svg>
				</button>
			</div>

			<div class="messenger-chat-messages">
				${messagesHtml}
				${
					isClosed
						? `
				<div class="messenger-closed-banner">
					<i class="ph ph-check-circle"></i>
					<span>This conversation has been resolved</span>
				</div>
				`
						: ''
				}
				<div class="messenger-typing-indicator">
					<div class="messenger-typing-dots">
						<span></span><span></span><span></span>
					</div>
					<span class="messenger-typing-text"></span>
				</div>
			</div>

			${
				isClosed
					? ''
					: `
			<div class="messenger-compose-attachments-preview"></div>

			<div class="messenger-chat-compose">
<button class="sdk-btn-icon messenger-compose-attach" aria-label="Attach file">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width="20" height="20">
    <rect width="256" height="256" fill="none"/>
    <path d="M160,80,76.69,164.69a16,16,0,0,0,22.63,22.62L198.63,86.63a32,32,0,0,0-45.26-45.26L54.06,142.06a48,48,0,0,0,67.88,67.88L204,128" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
  </svg>
</button>				<div class="messenger-compose-input-wrapper">
					<textarea class="messenger-compose-input" placeholder="${placeholder}" rows="1"></textarea>
				</div>
				<button class="messenger-compose-send" aria-label="Send" disabled>
					<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
						<path d="M227.32,28.68a16,16,0,0,0-15.66-4.08l-.15,0L19.57,82.84a16,16,0,0,0-2.49,29.8L102,154l41.3,84.87A15.86,15.86,0,0,0,157.74,248q.69,0,1.38-.06a15.88,15.88,0,0,0,14-11.51l58.2-191.94c0-.05,0-.1,0-.15A16,16,0,0,0,227.32,28.68ZM157.83,231.85l-.05.14L118.42,148.9l47.24-47.25a8,8,0,0,0-11.31-11.31L107.1,137.58,24,98.22l.14,0L216,40Z"></path>
					</svg>
				</button>
				<input type="file" class="messenger-compose-file-input" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip" />
			</div>
			`
			}
		`;

		this._typingIndicator = this.element.querySelector(
			'.messenger-typing-indicator'
		);
		this._attachEvents();
		this._scrollToBottom();
		this._renderAttachmentPreviews();
	}

	_renderEmptyState(isNewConversation = false) {
		const avatarHtml = this._renderTeamAvatars();
		const responseTime =
			this.state.responseTime || 'We typically reply within a few minutes';
		const isOnline = this.state.agentsOnline;

		return `
			<div class="messenger-chat-empty">
				<div class="messenger-chat-empty-avatars">${avatarHtml}</div>
				<h3>${isNewConversation ? 'Start a new conversation' : 'Start the conversation'}</h3>
				<p>Send us a message and we'll get back to you as soon as possible.</p>
				<div class="messenger-chat-availability">
					<span class="messenger-availability-dot ${isOnline ? 'messenger-availability-online' : 'messenger-availability-away'}"></span>
					<span>${isOnline ? "We're online now" : responseTime}</span>
				</div>
			</div>
		`;
	}

	_renderMessageAttachments(attachments) {
		if (!attachments || attachments.length === 0) return '';
		return attachments
			.map((att) => {
				if (att.type === 'image') {
					return `<img class="messenger-message-image" src="${this._escapeHtml(att.url)}" alt="${this._escapeHtml(att.name || 'image')}" data-url="${this._escapeHtml(att.url)}" />`;
				}
				return `<a class="messenger-message-file" href="${this._escapeHtml(att.url)}" data-url="${this._escapeHtml(att.url)}" data-name="${this._escapeHtml(att.name || 'file')}">
				<i class="ph ph-file"></i>
				<span>${this._escapeHtml(att.name || 'file')}</span>
				<i class="ph ph-download-simple messenger-file-download-icon"></i>
			</a>`;
			})
			.join('');
	}

	_renderMessage(message) {
		const isOwn = message.isOwn;
		const messageClass = isOwn
			? 'messenger-message-own'
			: 'messenger-message-received';
		const timeStr = this._formatMessageTime(message.timestamp);
		const attachmentsHtml = this._renderMessageAttachments(message.attachments);

		const contentHtml = message.content
			? `<div class="messenger-message-content">${this._formatMessageContent(message.content)}</div>`
			: '';
		const bubbleHtml = contentHtml
			? `<div class="messenger-message-bubble">${contentHtml}</div>`
			: '';

		if (isOwn) {
			return `
				<div class="messenger-message ${messageClass}">
					${bubbleHtml}
					${attachmentsHtml}
					<div class="messenger-message-time">${timeStr}</div>
				</div>
			`;
		}

		const avatarHtml = this._renderSenderAvatar(message.sender);
		return `
			<div class="messenger-message ${messageClass}">
				<div class="messenger-message-sender">${message.sender?.name || 'Support'}</div>
				<div class="messenger-message-row">
					<div class="messenger-message-avatar">${avatarHtml}</div>
					<div class="messenger-message-wrapper">
						${bubbleHtml}
						${attachmentsHtml}
					</div>
				</div>
				<div class="messenger-message-time">${timeStr}</div>
			</div>
		`;
	}

	_renderSenderAvatar(sender) {
		if (sender?.avatarUrl) {
			return `<div class="sdk-avatar sdk-avatar-sm"><img src="${sender.avatarUrl}" alt="${sender.name}" /></div>`;
		}
		const initial = (sender?.name || 'S').charAt(0).toUpperCase();
		return `<div class="sdk-avatar sdk-avatar-sm">${initial}</div>`;
	}

	_renderTeamAvatars() {
		const avatars = this.state.teamAvatars;
		if (!avatars || avatars.length === 0) {
			return `
				<div class="messenger-avatar-stack">
					<div class="sdk-avatar sdk-avatar-md">S</div>
					<div class="sdk-avatar sdk-avatar-md">T</div>
				</div>
			`;
		}

		const avatarItems = avatars
			.slice(0, 3)
			.map((avatar) => {
				if (typeof avatar === 'string' && avatar.startsWith('http')) {
					return `<div class="sdk-avatar sdk-avatar-md"><img src="${avatar}" alt="Team member" /></div>`;
				}
				return `<div class="sdk-avatar sdk-avatar-md">${avatar.charAt(0).toUpperCase()}</div>`;
			})
			.join('');

		return `<div class="messenger-avatar-stack">${avatarItems}</div>`;
	}

	_formatMessageTime(timestamp) {
		if (!timestamp) return '';
		const date = new Date(timestamp);
		return date.toLocaleTimeString('en-US', {
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
		});
	}

	_formatMessageContent(content) {
		if (!content) return '';
		return content
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/\n/g, '<br>');
	}

	_appendMessage(message) {
		const messagesContainer = this.element.querySelector(
			'.messenger-chat-messages'
		);
		const emptyState = messagesContainer.querySelector('.messenger-chat-empty');
		if (emptyState) {
			emptyState.remove();
		}

		const messageHtml = this._renderMessage(message);
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = messageHtml;
		messagesContainer.appendChild(tempDiv.firstElementChild);
	}

	_scrollToBottom() {
		const messagesContainer = this.element.querySelector(
			'.messenger-chat-messages'
		);
		if (messagesContainer) {
			setTimeout(() => {
				messagesContainer.scrollTop = messagesContainer.scrollHeight;
			}, 50);
		}
	}

	_updateSendButtonState() {
		const input = this.element.querySelector('.messenger-compose-input');
		const sendBtn = this.element.querySelector('.messenger-compose-send');
		if (input && sendBtn) {
			sendBtn.disabled =
				!input.value.trim() && this._pendingAttachments.length === 0;
		}
	}

	_renderAttachmentPreviews() {
		const container = this.element.querySelector(
			'.messenger-compose-attachments-preview'
		);
		if (!container) return;

		if (this._pendingAttachments.length === 0) {
			container.innerHTML = '';
			container.style.display = 'none';
			return;
		}

		container.style.display = 'flex';
		container.innerHTML = this._pendingAttachments
			.map((att, i) => {
				const isImage = att.type.startsWith('image');
				const thumb = isImage
					? `<img class="messenger-attachment-thumb" src="${att.preview}" alt="${this._escapeHtml(att.file.name)}" />`
					: `<div class="messenger-attachment-thumb messenger-attachment-file-icon"><i class="ph ph-file"></i></div>`;
				return `
				<div class="messenger-attachment-preview" data-index="${i}">
					${thumb}
					<button class="messenger-attachment-remove" data-index="${i}" aria-label="Remove">&times;</button>
				</div>
			`;
			})
			.join('');

		container
			.querySelectorAll('.messenger-attachment-remove')
			.forEach((btn) => {
				btn.addEventListener('click', (e) => {
					const idx = parseInt(e.currentTarget.dataset.index, 10);
					this._pendingAttachments.splice(idx, 1);
					this._renderAttachmentPreviews();
					this._updateSendButtonState();
				});
			});
	}

	_attachEvents() {
		this.element
			.querySelector('.messenger-back-btn')
			.addEventListener('click', () => {
				this.state.setView('messages');
			});

		this.element
			.querySelector('.sdk-close-btn')
			.addEventListener('click', () => {
				this.state.setOpen(false);
			});

		const input = this.element.querySelector('.messenger-compose-input');
		const sendBtn = this.element.querySelector('.messenger-compose-send');

		if (input && sendBtn) {
			input.addEventListener('input', () => {
				input.style.height = 'auto';
				input.style.height = Math.min(input.scrollHeight, 120) + 'px';
				this._updateSendButtonState();

				if (input.value.trim()) {
					this._startTyping();
				}
			});

			input.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' && !e.shiftKey) {
					e.preventDefault();
					this._sendMessage();
				}
			});

			sendBtn.addEventListener('click', () => {
				this._sendMessage();
			});
		}

		const attachBtn = this.element.querySelector('.messenger-compose-attach');
		const fileInput = this.element.querySelector(
			'.messenger-compose-file-input'
		);

		if (attachBtn && fileInput) {
			attachBtn.addEventListener('click', () => {
				fileInput.click();
			});

			fileInput.addEventListener('change', (e) => {
				const files = e.target.files;
				if (!files) return;
				Array.from(files).forEach((file) => {
					const reader = new FileReader();
					reader.onload = (ev) => {
						this._pendingAttachments.push({
							file,
							preview: ev.target.result,
							type: file.type,
						});
						this._renderAttachmentPreviews();
						this._updateSendButtonState();
					};
					reader.readAsDataURL(file);
				});
				fileInput.value = '';
			});
		}

		const messagesContainer = this.element.querySelector(
			'.messenger-chat-messages'
		);
		if (messagesContainer) {
			messagesContainer.addEventListener('click', (e) => {
				const fileLink = e.target.closest('.messenger-message-file');
				if (fileLink) {
					e.preventDefault();
					const url = fileLink.dataset.url;
					const name = fileLink.dataset.name;
					this._downloadFile(url, name);
					return;
				}

				const img = e.target.closest('.messenger-message-image');
				if (img) {
					const url = img.dataset.url || img.src;
					window.open(url, '_blank');
				}
			});
		}
	}

	async _downloadFile(url, name) {
		try {
			const response = await fetch(url);
			const blob = await response.blob();
			const blobUrl = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = blobUrl;
			a.download = name || 'download';
			a.style.display = 'none';
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(blobUrl);
		} catch {
			window.open(url, '_blank');
		}
	}

	_escapeHtml(text) {
		if (!text) return '';
		return text
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	async _sendMessage() {
		if (this._isConversationClosed) return;

		const input = this.element.querySelector('.messenger-compose-input');
		const content = input.value.trim();
		const hasAttachments = this._pendingAttachments.length > 0;

		if (!content && !hasAttachments) return;

		this._stopTyping();

		const attachmentsToSend = [...this._pendingAttachments];
		const isNewConversation = !this.state.activeConversationId;

		if (isNewConversation) {
			if (this.options.onStartConversation) {
				this.options.onStartConversation(content, attachmentsToSend);
			}
		} else {
			const message = {
				id: 'msg_' + Date.now(),
				content: content,
				isOwn: true,
				isOptimistic: true,
				timestamp: new Date().toISOString(),
				attachments: attachmentsToSend.map((a) => ({
					url: a.preview,
					type: a.type.startsWith('image') ? 'image' : 'file',
					name: a.file.name,
				})),
			};

			this.state.addMessage(this.state.activeConversationId, message);

			if (this.options.onSendMessage) {
				this.options.onSendMessage(
					this.state.activeConversationId,
					message,
					attachmentsToSend
				);
			}
		}

		input.value = '';
		input.style.height = 'auto';
		this._pendingAttachments = [];
		this._renderAttachmentPreviews();
		this._updateSendButtonState();
	}

	_startTyping() {
		if (this._isConversationClosed) return;
		if (!this._isTyping && this.state.activeConversationId) {
			this._isTyping = true;
			if (this.options.onTyping) {
				this.options.onTyping(this.state.activeConversationId, true);
			}
		}

		if (this._typingTimeout) {
			clearTimeout(this._typingTimeout);
		}
		this._typingTimeout = setTimeout(() => {
			this._stopTyping();
		}, 3000);
	}

	_stopTyping() {
		if (this._isTyping && this.state.activeConversationId) {
			this._isTyping = false;
			if (this.options.onTyping) {
				this.options.onTyping(this.state.activeConversationId, false);
			}
		}
		if (this._typingTimeout) {
			clearTimeout(this._typingTimeout);
			this._typingTimeout = null;
		}
	}

	_showTypingIndicator(userName) {
		if (this._typingIndicator) {
			this._typingIndicator.style.display = 'flex';
			const textEl = this._typingIndicator.querySelector(
				'.messenger-typing-text'
			);
			if (textEl) {
				textEl.textContent = `${userName || 'Support'} is typing...`;
			}
			this._scrollToBottom();
		}
	}

	_hideTypingIndicator() {
		if (this._typingIndicator) {
			this._typingIndicator.style.display = 'none';
		}
	}

	destroy() {
		if (this._unsubscribe) {
			this._unsubscribe();
		}
		if (this._typingTimeout) {
			clearTimeout(this._typingTimeout);
		}
		this._stopTyping();
		if (this.element && this.element.parentNode) {
			this.element.parentNode.removeChild(this.element);
		}
	}
}
